import * as d3 from "d3";
import _ from "lodash";
import moment from "moment";
import datetimeManager from "../managers/datetime";
import mathManager from "../managers/math";
import commonRenderer from "../renderers/common";
import {
  CHART_LAYER_MID_PRICE,
  CHART_LAYER_LEVELS_TOP,
  CHART_LAYER_LEVELS_TOP_3,
  CHART_LAYER_LEVELS_TOP_5,
  CHART_LAYER_LEVELS_TOP_10,
  API_KEYS_SPREAD
} from "../managers/constants";
import { CHART_LABEL_TIME, CHART_LABEL_MID_PRICE, CHART_LABEL_SPREAD_TICKS } from "../managers/string-literals";

// Note: This file is still under progress and therefore please do not review it yet

const service = (() => {
  const render = (params) => {
    // View / hide layers on the drawing
    let showMidPriceLineLayer = _.includes(params.selectedLayers, CHART_LAYER_MID_PRICE) ? true : false;
    let showTopOfTheBookLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP) ? true : false;
    let showTop3LevelsLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP_3) ? true : false;
    let showTop5LevelsLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP_5) ? true : false;
    let showTop10LevelsLayer = _.includes(params.selectedLayers, CHART_LAYER_LEVELS_TOP_10) ? true : false;

    let howManySpreadLines = 0;
    if (showTopOfTheBookLayer) howManySpreadLines = 1;
    if (showTop3LevelsLayer) howManySpreadLines = 3;
    if (showTop5LevelsLayer) howManySpreadLines = 5;
    if (showTop10LevelsLayer) howManySpreadLines = 10;

    // Dimensions
    let margin = params.dimensions.margin,
      width = params.dimensions.width,
      height = params.dimensions.height;

    let lineCurveAlpha = 0.5; // Curvature of lines
    /* let yDualAxisTooltip = null; */

    // Positioning for tick labels on axis
    let xAxisLabel = CHART_LABEL_TIME;
    let yAxisLabel = CHART_LABEL_SPREAD_TICKS;
    let dualYAxisLabel = CHART_LABEL_MID_PRICE;

    // Unique class for drag movements across X-axis
    let uniqueLineClass = "line" + params.uniqueKey;

    // Positioning for labels on axis
    let lineStrokeWidth = 1;

    //To show the scale of only visible layers.
    let yAxisScaleRangeProperties = [];
    if (showTopOfTheBookLayer) {
      yAxisScaleRangeProperties = [API_KEYS_SPREAD.l1];
    } else if (showTop3LevelsLayer) {
      yAxisScaleRangeProperties = [API_KEYS_SPREAD.l1, API_KEYS_SPREAD.l2, API_KEYS_SPREAD.l3];
    } else if (showTop5LevelsLayer) {
      yAxisScaleRangeProperties = [API_KEYS_SPREAD.l1, API_KEYS_SPREAD.l2, API_KEYS_SPREAD.l3, API_KEYS_SPREAD.l4, API_KEYS_SPREAD.l5];
    } else if (showTop10LevelsLayer) {
      yAxisScaleRangeProperties = [
        API_KEYS_SPREAD.l1,
        API_KEYS_SPREAD.l2,
        API_KEYS_SPREAD.l3,
        API_KEYS_SPREAD.l4,
        API_KEYS_SPREAD.l5,
        API_KEYS_SPREAD.l6,
        API_KEYS_SPREAD.l7,
        API_KEYS_SPREAD.l8,
        API_KEYS_SPREAD.l9,
        API_KEYS_SPREAD.l10
      ];
    }
    // ==================================================
    // CREATE SVG
    // ==================================================

    let svg = commonRenderer.createSvg(params.drawingContainerElementId, width, height, margin);

    // ==================================================
    // CREATE SCALE
    // ==================================================

    // Time scale for X axis
    let x = d3
      .scaleTime()
      .range([1, width])
      .domain(
        d3.extent(params.data, function(d) {
          return datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset);
        })
      );

    // Linear scale for Y axis
    let y = d3
      .scaleLinear()
      .range([height, 1])
      .domain(commonRenderer.getMinMaxValues(params.data, yAxisScaleRangeProperties))
      .nice();

    // Linear scale for Y-Dual axis
    let dualY = d3
      .scaleLinear()
      .range([height, 1])
      .domain(commonRenderer.getMinMaxValues(params.data, API_KEYS_SPREAD.midPrice))
      .nice();

    // ==================================================
    // FIND MIN & MAX VALUES
    // ==================================================
    // For X axis
    var xDomain = d3.extent(params.data, function(d) {
      return datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset);
    });

    // ==================================================
    // CREATE AXES
    // ==================================================

    // Create X axis
    let xAxis = commonRenderer.createXAxisTimeScale(
      x,
      commonRenderer.generateXAxisTickValues(params.data, params.zoneOffset, params.granularityCode, width),
      params.tickFormat
    );

    // Create Y axis
    let yAxis = commonRenderer.createYAxisLinearScale(y);

    // Create Y-Dual axis
    let dualYAxis = commonRenderer.createDualYAxisLinearScale(dualY);

    // ==================================================
    // DRAW AXES
    // ==================================================
    // Draw X axis
    commonRenderer.drawXAxis(svg, height, xAxis);

    // Draw Y axis
    commonRenderer.drawYAxis(svg, yAxis);

    if (showMidPriceLineLayer) {
      // Draw Y-Dual axis
      commonRenderer.drawDualYAxis(svg, width, dualYAxis);
    }

    // ==================================================
    // DRAW LABEL on AXES
    // ==================================================

    // Label for X axis
    commonRenderer.drawXAxisLabel(svg, width, height, xAxisLabel);

    // Label for Y axis
    commonRenderer.drawYAxisLabel(svg, height, yAxisLabel, margin);

    if (showMidPriceLineLayer) {
      // Label for Y-Dual axis
      commonRenderer.drawYDualAxisLabel(svg, width, height, dualYAxisLabel, margin);
    }

    // ==================================================
    // CREATE COMMON TOOLTIP
    // ==================================================
    //common tooltip
    let tooltip = commonRenderer.drawChartDataTooltip(params.drawingContainerElementId, params.uniqueKey);

    // ==================================================
    // CREATE TOOLTIP FOR TOP OF THE BOOK FOCUS LINE.
    // ==================================================
    //tooltip on y axis to follow focus line
    let yAxisTooltip = commonRenderer.drawChartTooltipYAxis(params.drawingContainerElementId, params.uniqueKey, "yAxisTooltip", margin, width);

    // ==================================================
    // HIGHLIGH ROLE SHIFT, ADD ALTERNATE BACKGROUND COLORS.
    // ==================================================
    commonRenderer.highlightRoleShift(params.data, params.zoneOffset, API_KEYS_SPREAD, svg, height, x);

    // ==================================================
    // SPREAD LINES
    // ==================================================

    let spreadLevels = [];
    for (let i = 1; i <= howManySpreadLines; i++) {
      //function to draw spread line.
      spreadLevels[i] = d3
        .line()
        .x(function(d) {
          return x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset));
        })
        .y(function(d) {
          return y(d[API_KEYS_SPREAD["l" + i]]);
        })
        .curve(d3.curveCatmullRom.alpha(lineCurveAlpha));

      //Drawing spread Line.
      svg
        .append("path")
        .datum(
          params.data.filter(function(obj) {
            return obj[API_KEYS_SPREAD["l" + i]] ? true : false;
          })
        )
        .attr("class", "line spread-line-level" + i + " " + uniqueLineClass)
        .attr("stroke-width", lineStrokeWidth)
        .attr("d", spreadLevels[i]);
    }

    // ==================================================
    // MID PRICE LINE
    // ==================================================

    if (showMidPriceLineLayer) {
      //function to draw Mid Price line.
      let midPriceLine = d3
        .line()
        .x(function(d) {
          return x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset));
        })
        .y(function(d) {
          return dualY(d[API_KEYS_SPREAD.midPrice]);
        })
        .curve(d3.curveCatmullRom.alpha(lineCurveAlpha));

      //Drawing Mid-price Line.
      svg
        .append("path")
        .datum(
          params.data.filter(function(obj) {
            return obj[API_KEYS_SPREAD.midPrice] ? true : false;
          })
        )
        .attr("class", "line mid-price-line " + uniqueLineClass)
        .attr("d", midPriceLine);
    }

    // ==================================================
    // FOCUS LINES
    // ==================================================

    var focus = svg.append("g").style("display", "none");
    //Creating X axis focus line
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineX");
    //Creating top of the book focus line
    commonRenderer.drawFocusLine(focus, params.uniqueKey, "focusLineYTopOfTheBook");
    //Create Top of the book focus circle for point highlighting
    commonRenderer.drawFocusCircle(focus, params.uniqueKey, "focusCircleTopOfTheBook", "focus-point-spread");

    var bisectDate = d3.bisector(function(d) {
      return d[API_KEYS_SPREAD.timestamp];
    }).left;

    //drawing RECT in SVG to track mouse position on it
    svg
      .append("rect")
      .attr("class", "overlay")
      .attr("width", width)
      .attr("height", height)
      .on("mouseover", function() {
        focus.style("display", null);
      })
      .on("mouseout", function() {
        tooltip.style("display", "none");
        yAxisTooltip.style("display", "none");
        focus.style("display", "none");
      })
      .on("mousemove", function() {
        //getting current mouse position coordinates.
        var mouse = d3.mouse(this);
        var mouseDate = x.invert(mouse[0]);
        var i = bisectDate(params.data, moment(mouseDate).unix()); // returns the index to the current data item
        i = i >= params.data.length ? params.data.length - 1 : i;

        var d0 = params.data[i - 1]; //previous point
        var d1 = params.data[i]; //current point

        // Used to calculate difference between current data points and previous data points
        let previousDataObject = undefined;

        // work out which date value is closest to the mouse
        if (d0 !== undefined) {
          if (moment(mouseDate).unix() - d0[API_KEYS_SPREAD.timestamp] > d1[API_KEYS_SPREAD.timestamp] - moment(mouseDate).unix()) {
            var d = d1;
            previousDataObject = d0;
          } else {
            var d = d0;
            previousDataObject = params.data[i - 2];
          }
          //var d = moment(mouseDate).unix() - d0[API_KEYS_SPREAD.timestamp] > d1[API_KEYS_SPREAD.timestamp] - moment(mouseDate).unix() ? d1 : d0;
        } else {
          var d = d1;
        }

        var hoveredXValue = x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset)); //getting x value of current mouse position

        // ==================================================
        // DRAW COMMON TOOLTIP
        // ==================================================

        let tooltipContent =
          "<tr><td colspan=3>" +
          datetimeManager.getWholeDateTime(
            d[API_KEYS_SPREAD.timestamp],
            params.zoneOffset,
            commonRenderer.getXAxisDateFormat(params.granularityCode)
          ) +
          " " +
          params.zoneName +
          " (" +
          params.zoneTiming +
          ")" +
          "</td></tr>";

        tooltipContent += "<tr><td>Contract:</td><td>" + d[API_KEYS_SPREAD.contract] + "</td></tr>";

        if (showMidPriceLineLayer) {
          tooltipContent +=
            "<tr><td>MidPrice:</td><td>" +
            mathManager.toUSANumberFormat(d[API_KEYS_SPREAD.midPrice]) +
            "</td><td>" +
            commonRenderer.getPercentageChange(
              previousDataObject ? previousDataObject[API_KEYS_SPREAD.midPrice] : undefined,
              d[API_KEYS_SPREAD.midPrice],
              params.granularityCode
            ) +
            "</td></tr>";
        }

        let howManyLevels = 1;
        if (showTop3LevelsLayer) howManyLevels = 3;
        else if (showTop5LevelsLayer) howManyLevels = 5;
        else if (showTop10LevelsLayer) howManyLevels = 10;
        const inversePercentageIndication = true;

        for (let levelIndex = 1; levelIndex <= howManyLevels; levelIndex++) {
          let spreadVal = d[API_KEYS_SPREAD["l" + levelIndex]];
          let previousSpreadVal = previousDataObject ? previousDataObject[API_KEYS_SPREAD["l" + levelIndex]] : undefined;

          tooltipContent +=
            "<tr><td>" +
            (levelIndex === 1 ? "Top Of The Book" : "Level " + levelIndex) +
            ":</td><td>" +
            mathManager.toUSANumberFormat(commonRenderer.truncateDecimalPointsByChartType(spreadVal, params.primaryChart)) +
            "</td><td>" +
            commonRenderer.getPercentageChange(previousSpreadVal, spreadVal, params.granularityCode, inversePercentageIndication) +
            "</td></tr>";
        }

        //mouse[0] is the x co-ordinate of current mouse position.
        commonRenderer.positionChartDataTooltip(width / 2, mouse[0], tooltip, tooltipContent, margin);

        // ==================================================
        // DRAW X AXIS (VERTICAL) FOCUS LINE.
        // ==================================================
        //drawing X line (vertical line)
        commonRenderer.positionXAxisFocusLine(focus, params.uniqueKey, "focusLineX", hoveredXValue, height);

        // =====================================================================================================
        // DRAW TOP OF THE BOOK FOCUS LINE & TOOLTIP ON IT AND DRAW TOP OF THE BOOK FOCUS CIRCLE (HOVERED POINT)
        // =====================================================================================================
        if (d[API_KEYS_SPREAD.l1]) {
          commonRenderer.positionYAxisFocusLine(
            focus,
            params.uniqueKey,
            "focusLineYTopOfTheBook",
            x(xDomain[0]),
            y(d[API_KEYS_SPREAD.l1]),
            x(xDomain[1]),
            y(d[API_KEYS_SPREAD.l1])
          );

          commonRenderer.positionChartTooltipAxis(yAxisTooltip, d[API_KEYS_SPREAD.l1], y(d[API_KEYS_SPREAD.l1]), params.primaryChart);

          commonRenderer.positionFocusCircle(
            focus,
            params.uniqueKey,
            "focusCircleTopOfTheBook",
            x(datetimeManager.getMomentObject(d[API_KEYS_SPREAD.timestamp], params.zoneOffset)),
            y(d[API_KEYS_SPREAD.l1])
          );
        } else {
          focus.select("#focusLineYTopOfTheBook" + params.uniqueKey).style("display", "none");
          yAxisTooltip.style("display", "none");
          focus.select("#focusCircleTopOfTheBook" + params.uniqueKey).style("display", "none");
        }
      });
  }; //End of Render function.

  return { render };
})();

export default service;



// WEBPACK FOOTER //
// ./src/services/renderers/spread-line.js